import {inject, observer} from 'mobx-react';
import React, {useEffect, useState} from 'react';
import GenerateMicrolessonUI from '../view/GenerateMicrolessonUI';
import GenerateMicrolessonStore from '../store/GenerateMicrolessonStore';
import {useNavigate, useParams} from 'react-router-dom';
import {LessonGenerationFromPromptModel} from '../../../../data/models/AI/LessonGenerationModel';
import MyLibraryModalStore from '../../../../modules/MyLibraryModal/store/MyLibraryModalStore';
import {
  MEDIA_FILES_ALL_VIDEO,
  MEDIA_FILES_DOCUMENT,
  MediaFile,
} from '../../../../data/models/LibraryFile/MediaFile';
import {MyLibraryModal} from '../../../../modules/MyLibraryModal';
import {Gender} from '../../../../data/models/AI/VideoGenerationOptions';

interface Props {
  generateMicrolessonStore?: GenerateMicrolessonStore;
  myLibraryModalStore?: MyLibraryModalStore;
}

const GenerateMicrolessonContainer = inject(
  'generateMicrolessonStore',
  'myLibraryModalStore',
)(
  observer(({generateMicrolessonStore, myLibraryModalStore}: Props) => {
    const [estimatedCost, setEstimatedCost] = useState(0);
    const [availableCredits, setAvailableCredits] = useState(0);
    const [isGenerationAvailable, setGenerationAvailable] = useState(false);
    const [llmProvider, setLlmProvider] = useState<string>('openai');
    const {organizationId} = useParams();
    const navigate = useNavigate();

    useEffect(() => {
      const checkAvailability = async (orgId: string) => {
        const result =
          await generateMicrolessonStore?.checkGenerationAvailability(orgId);
        setGenerationAvailable(result ?? false);
      };

      const fetchLlmProvider = async (orgId: string) => {
        const provider =
          await generateMicrolessonStore?.getOrganizationLlmProvider(orgId);
        setLlmProvider(provider ?? 'openai');
      };

      if (organizationId) {
        checkAvailability(organizationId);
        fetchLlmProvider(organizationId);
      }
    }, [generateMicrolessonStore, organizationId]);

    const onCancel = () => {
      navigate(-1);
    };

    const onOpenLibrary = (
      fileType: 'video' | 'document',
      selectedFile?: MediaFile,
    ): Promise<MediaFile> => {
      const filter =
        fileType === 'document' ? MEDIA_FILES_DOCUMENT : MEDIA_FILES_ALL_VIDEO;

      return new Promise(res => {
        myLibraryModalStore!.showLibrary(
          filter,
          files => res(files[0]),
          false,
          selectedFile ? [selectedFile] : [],
        );
      });
    };

    const onGenerateFromPrompt = (model: LessonGenerationFromPromptModel) => {
      if (organizationId) {
        generateMicrolessonStore
          ?.generateLessonFromPrompt(organizationId, model)
          .then(success => {
            if (success) {
              onCancel();
            }
          });
      }
    };

    const onGenerateFromVideo = (
      includeBranch: boolean,
      includeGrading: boolean,
      contentId: string,
      language: string,
      dialect: string,
      gender: Gender,
      name: string,
      passingScore?: number,
      addAckNode?: boolean,
      mediaType: 'video' | 'document' = 'video',
    ) => {
      if (organizationId) {
        generateMicrolessonStore
          ?.generateLessonFromVideo(
            organizationId,
            includeBranch,
            includeGrading,
            contentId,
            language,
            dialect,
            gender,
            name,
            passingScore,
            addAckNode,
            mediaType,
          )
          .then(success => {
            if (success) {
              onCancel();
            }
          });
      }
    };

    const onCalculateEstimation = (prompt: string, language: string) => {
      if (organizationId) {
        generateMicrolessonStore!
          .getMicrolessonGenerationCost(organizationId, {
            prompt,
            language,
          })
          .then(res => {
            setAvailableCredits(res.availableCredits);
            setEstimatedCost(res.estimatedCost);
          });
      }
    };

    return (
      <>
        <GenerateMicrolessonUI
          isLoading={generateMicrolessonStore!.isLoading}
          onCancel={onCancel}
          onGenerateFromPrompt={onGenerateFromPrompt}
          onOpenLibrary={onOpenLibrary}
          onGenerateFromVideo={onGenerateFromVideo}
          calculateEstimation={onCalculateEstimation}
          estimatedCost={estimatedCost}
          availableCredits={availableCredits}
          isEstimationLoading={generateMicrolessonStore!.isEstimationLoading}
          isGenerationAvailable={isGenerationAvailable}
          llmProvider={llmProvider}
        />
        <MyLibraryModal />
      </>
    );
  }),
);

export default GenerateMicrolessonContainer;
